import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionHelp from '@/components/help/SectionHelp';
import { User, Clock, ArrowLeft, FileText, CalendarDays, ListChecks, LayoutGrid, Star, Sparkles, Repeat, DollarSign } from 'lucide-react';

const TYPE_ICONS = { lead: FileText, deal: LayoutGrid, task: ListChecks, appointment: CalendarDays, activity: Clock };
const TYPE_COLORS = { lead: 'text-blue-600 bg-blue-50', deal: 'text-emerald-600 bg-emerald-50', task: 'text-purple-600 bg-purple-50', appointment: 'text-amber-600 bg-amber-50', activity: 'text-gray-500 bg-gray-50' };

export default function CrmContactTimeline() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rec, setRec] = useState(null);   // recurrencia (Persona 360, Fase 1)

  const load = async () => {
    try {
      const res = await api.get(`/tenant/crm/contacts/${contactId}/timeline?page=${page}&page_size=20`);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Error cargando timeline'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [contactId, page]);

  // Recurrencia por persona: se pide aparte para no frenar el timeline.
  useEffect(() => {
    let vivo = true;
    api.get(`/tenant/crm/contacts/${contactId}/recurrencia`)
      .then((r) => { if (vivo) setRec(r.data); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [contactId]);

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="crm-contact-timeline">
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-3.5 h-3.5" /></Button>
        <div>
          <h2 className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><User className="w-4 h-4" /> Timeline del contacto</h2>
          <p className="text-xs text-[#64748B]">{total} eventos</p>
        </div>
      </div>

      {rec && (
        <div className="rounded-lg border border-[#E2E8F0] bg-white p-4" data-testid="contact-recurrencia">
          <div className="flex items-center gap-2 mb-3">
            {rec.es_recurrente ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                <Star className="w-3.5 h-3.5" /> Cliente recurrente
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                <Sparkles className="w-3.5 h-3.5" /> {rec.num_visitas > 0 ? 'Cliente' : 'Primera visita'}
              </span>
            )}
            {rec.num_visitas > 0 && (
              <span className="text-xs text-[#64748B]">{rec.num_visitas} {rec.num_visitas === 1 ? 'visita' : 'visitas'}</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            <div>
              <div className="text-[#94A3B8]">Última visita</div>
              <div className="text-[#0F172A] font-medium">
                {rec.ultima_visita?.fecha
                  ? new Date(rec.ultima_visita.fecha).toLocaleDateString('es-MX', { dateStyle: 'medium' })
                  : '—'}
                {rec.ultima_visita?.servicio ? ` · ${rec.ultima_visita.servicio}` : ''}
              </div>
            </div>
            <div>
              <div className="text-[#94A3B8]">Próxima cita</div>
              <div className="text-[#0F172A] font-medium">
                {rec.proxima_visita?.fecha
                  ? new Date(rec.proxima_visita.fecha).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
                  : '—'}
                {rec.proxima_visita?.profesional ? ` · ${rec.proxima_visita.profesional}` : ''}
              </div>
            </div>
            <div>
              <div className="text-[#94A3B8] flex items-center gap-1"><DollarSign className="w-3 h-3" /> Valor histórico</div>
              <div className="text-[#0F172A] font-medium">
                ${Number(rec.valor_historico || 0).toLocaleString('es-MX')} {rec.moneda}
              </div>
            </div>
            {rec.profesional_habitual && (
              <div>
                <div className="text-[#94A3B8] flex items-center gap-1"><User className="w-3 h-3" /> Profesional habitual</div>
                <div className="text-[#0F172A] font-medium">{rec.profesional_habitual}</div>
              </div>
            )}
            {rec.frecuencia_media_dias != null && (
              <div>
                <div className="text-[#94A3B8] flex items-center gap-1"><Repeat className="w-3 h-3" /> Frecuencia</div>
                <div className="text-[#0F172A] font-medium">cada ~{rec.frecuencia_media_dias} días</div>
              </div>
            )}
            {(rec.cancelaciones > 0 || rec.no_shows > 0) && (
              <div>
                <div className="text-[#94A3B8]">Cancelaciones / No-show</div>
                <div className="text-[#0F172A] font-medium">{rec.cancelaciones} / {rec.no_shows}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {items.length === 0 && <p className="text-sm text-[#64748B] text-center py-8">Sin actividad registrada.</p>}

      <div className="space-y-2" data-testid="contact-lead-history">
        {items.map((item, idx) => {
          const Icon = TYPE_ICONS[item.type] || Clock;
          const color = TYPE_COLORS[item.type] || 'text-gray-500 bg-gray-50';
          return (
            <div key={idx} className="flex gap-3 py-2 border-b border-[#F1F5F9] last:border-0" data-testid="contact-timeline-item">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#0F172A]">{item.title}</p>
                <p className="text-xs text-[#64748B]">{item.description}</p>
                <p className="text-[10px] text-[#94A3B8]">
                  {item.created_at ? new Date(item.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  {' · '}{item.type}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <span className="text-xs text-[#64748B] self-center">Pág. {page}</span>
          <Button size="sm" variant="outline" disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
        </div>
      )}
    </div>
  );
}
